import React, {useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import Resources from "../../../data/services/resources";
import {
    FLEET_SETTINGS_INCLUDED_LOOKUPS,
    READ_PERM,
    REQUIRED_DOCUMENTS_TYPES,
    UPDATE_PERM
} from "../../../util/util-constants";
import Page from "../../../common/components/layout/layout-components/page";
import PageHeader from "../../../common/components/layout/layout-components/page/page-header";
import PageHeaderInfo from "../../../common/components/layout/layout-components/page/page-header-info";
import NavResponsive from "../../../common/components/nav-responsive";
import DocumentEntitiesTab from "../../../common/components/tabs/document-entities-tab";
import DocumentTypesTab from "../../../common/components/tabs/document-types-tab";
import Lookups from "../../../common/components/tabs/lookupTab";
import Layout from "../../../common/components/layout";
import FuelTaxSettingsTab from "./fuel-tax-settings-tab";
import {checkPerm} from "../../../common/util/util-helpers";
import FleetSettingsGeneralTab from "./fleet-settings-tab";
import ModalConfirm from "../../../common/components/modal/modal-confirm";


export default function FleetSettingsView({translate, match, history, location}) {
    const dispatch = useDispatch();
    const ui = useSelector((state) => state.ui);
    const resource = useSelector((state) => state.resource);

    const [tabs, setTabs] = useState(() => getTabs());
    const currentTab = tabs.find(tab => tab.current) ?? {};

    const [breakpoint, setBreakpoint] = useState({});

    const [isTabDirty, setIsTabDirty] = useState(false);
    const [isDirtyStateWarningModalOpen, setIsDirtyStateWarningModalOpen] = useState(false);
    const [tabClicked, setTabClicked] = useState("");

    const handleTabChange = (resource) => {
        if (currentTab.resource === resource) {
            return false;
        }

        if (isTabDirty) {
            setIsDirtyStateWarningModalOpen(true);
            setTabClicked(resource)
        } else {
            setTabs(
                tabs.map((it) => {
                    it.current = it.resource === resource;
                    return it
                })
            )
        }
    }

    const handleBreakpointChange = (breakpoint) => {
        setBreakpoint(breakpoint)
    }

    return (
        <Layout
            ui={ui}
            match={match}
            history={history}
            location={location}
            translate={translate}
            isAccessible={checkPerm(Resources.MileageTaxSettings, UPDATE_PERM) || checkPerm(Resources.TruckDocuments, UPDATE_PERM) || checkPerm(Resources.TrailerDocuments, UPDATE_PERM)}
            dispatch={dispatch}
            hasFooter={currentTab?.name === 'RequiredDocuments' || currentTab?.name === 'general'}
            isDirty={isTabDirty}
            onBreakpoinChange={handleBreakpointChange}
        >
            <Page>
                <PageHeader
                    title={translate('page.heading.fleet_settings')}
                    afterTitle={(
                        <PageHeaderInfo
                            dispatch={dispatch}
                        />
                    )}
                />

                <NavResponsive
                    tabs={tabs}
                    onTabChange={handleTabChange}
                    translate={translate}
                />

                {currentTab.name === 'general' && (
                    <FleetSettingsGeneralTab
                        translate={translate}
                        setIsTabDirty={setIsTabDirty}
                        resourcePath={currentTab.resource}
                    />
                )}

                {currentTab.name === 'fuel_and_taxes' && (
                    <div className="grid gap-4 py-8 max-w-3xl mx-auto">
                        <FuelTaxSettingsTab
                            setIsTabDirty={setIsTabDirty}
                            translate={translate}
                        />
                    </div>
                )}

                {currentTab.name === 'customFields' && (
                    <Lookups
                        customLookupFilter={(item, name) => {
                            if (name === "DocumentType") {
                                return !!item.IsFleetDocument
                            }
                            return true
                        }}
                        includeLookups={FLEET_SETTINGS_INCLUDED_LOOKUPS}
                        translate={translate}
                        resource={resource}
                        dispatch={dispatch}
                    />
                )}

                {currentTab.name === "DocumentTypes" && (
                    <DocumentTypesTab
                        translate={translate}
                        primaryType={'IsFleetDocument'}
                        breakpoint={breakpoint}
                    />
                )}

                {currentTab.name === "RequiredDocuments" && (
                    <DocumentEntitiesTab
                        requiredTypes={REQUIRED_DOCUMENTS_TYPES.Fleet}
                        translate={translate}
                        resource={resource}
                        dispatch={dispatch}
                        setIsTabDirty={setIsTabDirty}
                        sortOrder={['Truck', 'Trailer']}
                    />
                )}
            </Page>

            <ModalConfirm
                show={isDirtyStateWarningModalOpen}
                type="default"
                onClose={() => setIsDirtyStateWarningModalOpen(false)}
                onConfirm={() => {
                    setTabs(
                        tabs.map((it) => {
                            it.current = it.resource === tabClicked;
                            return it
                        })
                    );
                    setIsDirtyStateWarningModalOpen(false);
                    setIsTabDirty(false);
                }}
                closeButtonLabel={translate("btn.stay_on_current_tab")}
                buttonLabel={translate("text.Continue")}
                title={translate('dialog_heading.unsaved_changes_on_tab')}
                translate={translate}
            >
                <p className="text-base">{translate('text.changes_on_tab')}</p>
                <p className="text-base">{translate('text.changes_will_be_lost')}</p>
            </ModalConfirm>
        </Layout>
    )
}

const getTabs = () => {
    let hasCurrent = false;

    return [
        {
            name: 'general',
            resource: Resources.DispatchSettings,
            current: false,
            hasPerm: checkPerm(Resources.DispatchSettings, READ_PERM)
        },
        {
            name: 'fuel_and_taxes',
            resource: Resources.MileageTaxSettings,
            current: false,
            hasPerm: checkPerm(Resources.MileageTaxSettings, READ_PERM)
        },
        {
            name: 'customFields',
            resource: Resources.Lookup,
            current: false,
            hasPerm: checkPerm(Resources.Lookup, READ_PERM)
        },
        {
            name: 'DocumentTypes',
            resource: Resources.DocumentTypes,
            current: false,
            hasPerm: checkPerm(Resources.DocumentTypes, READ_PERM)
        },
        {
            name: 'RequiredDocuments',
            resource: Resources.DocumentEntities,
            current: false,
            hasPerm: checkPerm(Resources.DocumentEntities, READ_PERM)
        }
    ].map(tab => {
        if (!hasCurrent && !!tab.hasPerm) {
            hasCurrent = true;
            tab.current = true;
        }
        return tab;
    })
}
